// 図形上で右クリックしたときに表示するコンテキストメニュー。
//
// メニュー項目:
// - ロック / ロック解除（locked プロパティをトグル）
// - 最前面へ移動 (zOrder = 既存 max + 1)
// - 最背面へ移動 (zOrder = 既存 min - 1)
// - 削除（ロック中はメニューから「ロック解除」のみ可能）
//
// 表示位置はクライアント座標。画面端からはみ出す場合は自動でずらす。

import {
  DeleteIconData,
  FlipToBackIconData,
  FlipToFrontIconData,
  LockIconData,
  LockOpenIconData,
} from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

const MENU_WIDTH_PX = 200;
const MENU_MARGIN_PX = 8;

export function ObjectContextMenu(): JSX.Element | null {
  const contextMenu = useCanvasStore((s) => s.contextMenu);
  const closeContextMenu = useCanvasStore((s) => s.closeContextMenu);
  const objects = useCanvasDocStore((s) => s.objects);
  const dispatcher = useCanvasOpDispatcherContext();
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  // メニュー外クリック / Escape / スクロールで閉じる。
  useEffect(() => {
    if (!contextMenu) return undefined;
    const onDown = (event: MouseEvent | PointerEvent): void => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) return;
      closeContextMenu();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeContextMenu();
    };
    const onScroll = (): void => closeContextMenu();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onScroll, { passive: true });
    window.addEventListener("resize", closeContextMenu);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onScroll);
      window.removeEventListener("resize", closeContextMenu);
    };
  }, [contextMenu, closeContextMenu]);

  // 画面端で右や下へはみ出す場合は左 / 上へ寄せる。
  useLayoutEffect(() => {
    if (!contextMenu) {
      setPosition(null);
      return;
    }
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? MENU_WIDTH_PX;
    const menuHeight = menuEl?.offsetHeight ?? 200;
    const maxLeft = window.innerWidth - menuWidth - MENU_MARGIN_PX;
    const maxTop = window.innerHeight - menuHeight - MENU_MARGIN_PX;
    setPosition({
      left: Math.max(MENU_MARGIN_PX, Math.min(contextMenu.clientX, maxLeft)),
      top: Math.max(MENU_MARGIN_PX, Math.min(contextMenu.clientY, maxTop)),
    });
  }, [contextMenu]);

  if (!contextMenu) return null;
  const target = objects[contextMenu.objectId];
  if (!target) return null;

  const isLocked = target.locked === true;

  const handleToggleLock = (): void => {
    dispatcher.transformObject(target.id, { locked: !isLocked });
    closeContextMenu();
  };

  const handleBringToFront = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    const all = Object.values(objects);
    const maxZ = all.length > 0 ? Math.max(...all.map((o) => o.zOrder)) : 0;
    dispatcher.transformObject(target.id, { zOrder: maxZ + 1 });
    closeContextMenu();
  };

  const handleSendToBack = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    const all = Object.values(objects);
    const minZ = all.length > 0 ? Math.min(...all.map((o) => o.zOrder)) : 0;
    dispatcher.transformObject(target.id, { zOrder: minZ - 1 });
    closeContextMenu();
  };

  const handleDelete = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    dispatcher.deleteObjects([target.id]);
    useCanvasStore.getState().setSelectedObjectIds([]);
    closeContextMenu();
  };

  return (
    <ul
      ref={menuRef}
      role="menu"
      aria-label="図形のコンテキストメニュー"
      data-testid="object-context-menu"
      style={{
        position: "fixed",
        left: position?.left ?? contextMenu.clientX,
        top: position?.top ?? contextMenu.clientY,
        margin: 0,
        padding: "4px",
        listStyle: "none",
        minWidth: `${MENU_WIDTH_PX}px`,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-2)",
        zIndex: 1000,
        fontSize: "13px",
        color: "var(--color-text)",
      }}
    >
      <MenuItem
        icon={isLocked ? LockOpenIconData : LockIconData}
        label={isLocked ? "ロック解除" : "ロック"}
        onClick={handleToggleLock}
      />
      <MenuItem
        icon={FlipToFrontIconData}
        label="最前面へ移動"
        onClick={handleBringToFront}
        disabled={isLocked}
      />
      <MenuItem
        icon={FlipToBackIconData}
        label="最背面へ移動"
        onClick={handleSendToBack}
        disabled={isLocked}
      />
      <MenuItem
        icon={DeleteIconData}
        label="削除"
        onClick={handleDelete}
        disabled={isLocked}
        danger
      />
    </ul>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  readonly icon: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
}): JSX.Element {
  return (
    <li role="none" style={{ margin: 0 }}>
      <button
        role="menuitem"
        type="button"
        disabled={disabled}
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          padding: "8px 12px",
          background: "transparent",
          border: "none",
          borderRadius: "var(--radius-1)",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          color: danger && !disabled ? "#d22b2b" : "inherit",
          font: "inherit",
        }}
        onMouseEnter={(event) => {
          if (!disabled) event.currentTarget.style.background = "var(--color-surface-muted)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = "transparent";
        }}
      >
        <Icon d={icon} />
        <span>{label}</span>
      </button>
    </li>
  );
}
